import clsx from 'clsx'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'

import Avatar from '@/components-latest/primitives/Avatar'
import Button from '@/components-latest/primitives/Button'
import Checkbox from '@/components-latest/primitives/Controls/Checkbox'
import Label from '@/components-latest/primitives/Label'
import Separator from '@/components-latest/primitives/Separator'
import Text from '@/components-latest/primitives/Text'
import CommonModal from '@/components-latest/shared/Modals/CommonModal'
import { SupportedLanguages } from '@/config/i18n'
import { useFetchMenuListQuery } from '@/ducks/menu/api/menu.api'
import { IMenuItem } from '@/ducks/menu/menu.types'
import {
  selectCurrencyCode,
  selectStoreId
} from '@/ducks/store/selectors/store.selectors'

import Styles from './SelectMenuItemsModal.module.scss'

// The backend sends is_editable: false for an item whose discount Yassir created and that is on:
// a merchant cannot create, change or remove an item discount there (the whole request is refused with a 403).
const isYassirManaged = (item: IMenuItem) => item.offer?.is_editable === false

type IItemCheckBoxProps = {
  checked: boolean
  disabled: boolean
  onCheckboxToggle: (e: React.MouseEvent<HTMLButtonElement>) => void
  item: IMenuItem
}

const ItemCheckbox = ({
  checked,
  disabled,
  item,
  onCheckboxToggle
}: IItemCheckBoxProps) => {
  const { t, i18n } = useTranslation()

  const language = (i18n.resolvedLanguage || 'en') as SupportedLanguages
  const currency = useSelector(selectCurrencyCode)

  return (
    <div
      key={item._id}
      className={clsx(Styles.checkbox, { [Styles.disabled]: disabled })}
      title={
        disabled
          ? t('marketing.campaigns.details.managed-by-yassir')
          : undefined
      }>
      <Checkbox
        checked={checked}
        disabled={disabled}
        data-id={item._id}
        onClick={onCheckboxToggle}
        variant="branded"
        id={item._id}
      />
      <Label htmlFor={item._id}>
        <Avatar
          squared
          size="medium"
          image={item.avatar}
          string={item.name[language]}
        />
        <Text variant="nextSubtitle" weight="bold">
          {item.name[language]}
        </Text>
        {disabled && (
          <span className={Styles.yassirBadge} data-testid="item-by-yassir">
            {t('marketing.campaigns.by-yassir')}
          </span>
        )}
        <Text variant="nextSubtitle" className={Styles.price}>
          {item.price} {currency}
        </Text>
      </Label>
    </div>
  )
}

type Props = {
  isOpen: boolean
  onCloseModal: () => void
  onSubmit: (itemsIds: string[]) => void
  initialMenuItemsIds?: string[]
  multiple?: boolean
  // Item discount only: Buy X Get Y is a separate offer the backend does not block on these items.
  disableYassirManagedItems?: boolean
}

const SelectMenuItemsModal = ({
  isOpen,
  onCloseModal,
  onSubmit,
  initialMenuItemsIds = [],
  multiple = true,
  disableYassirManagedItems = false
}: Props) => {
  const storeId = useSelector(selectStoreId)
  const { data } = useFetchMenuListQuery({
    store_id: storeId as string
  })
  const { t, i18n } = useTranslation()

  const language = (i18n.resolvedLanguage || 'en') as SupportedLanguages

  const blockedIds = useMemo(() => {
    const ids = new Set<string>()
    if (!disableYassirManagedItems) return ids

    data?.menu?.mainCategories.forEach(category => {
      const items = [
        ...category.items,
        ...category.subCategories.flatMap(subcategory => subcategory.items)
      ]
      items.filter(isYassirManaged).forEach(item => ids.add(item._id))
    })
    return ids
  }, [data, disableYassirManagedItems])

  const [chosenIds, setChosenIds] = useState<string[]>([])
  useEffect(() => {
    // An item that became Yassir-managed since it was picked cannot be submitted, so it is dropped here.
    setChosenIds(initialMenuItemsIds.filter(id => !blockedIds.has(id)))
  }, [initialMenuItemsIds, blockedIds])

  const onCheckboxToggle = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      const itemId = e.currentTarget.dataset.id
      if (!itemId || blockedIds.has(itemId)) return

      setChosenIds(prev => {
        if (multiple) {
          return prev.includes(itemId)
            ? prev.filter(id => id !== itemId)
            : [...prev, itemId]
        }
        return [itemId]
      })
    },
    [multiple, blockedIds]
  )

  const isSubmitButtonDisabled =
    chosenIds.every(id => initialMenuItemsIds.includes(id)) &&
    chosenIds.length === initialMenuItemsIds.length

  const handleSubmit = useCallback(() => {
    onSubmit(chosenIds)
    onCloseModal()
  }, [onSubmit, chosenIds, onCloseModal])

  return (
    <CommonModal
      open={isOpen}
      title={t('offers.offers-selection-modal.title')}
      onOpenChange={onCloseModal}
      className={Styles.modal}
      scrollableBody
      scrollAreaClass={Styles.scrollable}
      body={
        <div>
          {data?.menu?.mainCategories?.length === 0 && (
            <Text align="center" variant="nextTitle-4">
              {t('offers.offers-selection-modal.empty-menu')}
            </Text>
          )}
          {data?.menu?.mainCategories.map(category => (
            <React.Fragment key={category._id}>
              <Text className={Styles.mainCategoryTitle} variant="nextTitle-3">
                {category.name[language]}
              </Text>
              {category.items.map(item => (
                <ItemCheckbox
                  key={item._id}
                  checked={chosenIds.includes(item._id)}
                  disabled={blockedIds.has(item._id)}
                  onCheckboxToggle={onCheckboxToggle}
                  item={item}
                />
              ))}
              {category.subCategories.map(subcategory => (
                <React.Fragment key={subcategory._id}>
                  <Text
                    className={Styles.subcategoryTitle}
                    weight="bold"
                    variant="nextSubtitle">
                    {subcategory.name[language]}
                  </Text>
                  {subcategory.items.map(item => (
                    <ItemCheckbox
                      key={item._id}
                      checked={chosenIds.includes(item._id)}
                      disabled={blockedIds.has(item._id)}
                      onCheckboxToggle={onCheckboxToggle}
                      item={item}
                    />
                  ))}
                </React.Fragment>
              ))}
              <Separator className={Styles.separator} />
            </React.Fragment>
          ))}
        </div>
      }
      footer={
        <Button
          className={Styles.submit}
          shape="rounded"
          size="medium"
          onClick={handleSubmit}
          disabled={isSubmitButtonDisabled}
          variant="branded">
          {t('offers.offers-selection-modal.submit-button')}
        </Button>
      }
    />
  )
}

export default SelectMenuItemsModal
